import {
  PROVIDER_LABELS,
  ProviderResultStatus,
  formatSoum,
  isPendingStatus,
  isSelectable,
  translate,
  type ProviderResult,
} from '@asko/shared';
import type { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ResultOtp } from './ResultOtp';

/**
 * Bitta provayder natijasi (BT §7.3, §7.4).
 *
 *   APPROVED     — yashil urg'u, limit va muddatlar, tanlash mumkin
 *   REJECTED     — sabab bilan, tanlab bo'lmaydi
 *   TIMEOUT      — «javob bermadi», tanlab bo'lmaydi
 *   AWAITING_OTP — provayder kod so'radi, shu yerda kiritiladi
 */

interface ResultCardProps {
  result: ProviderResult;
  onChoose: () => void;
  choosing: boolean;
  /** Boshqa provayder tanlangandan keyin tugmalar bloklanadi. */
  decided: boolean;
  /** Provayder so'ragan kod (`AWAITING_OTP`). */
  onConfirmOtp: (code: string) => void;
  onResendOtp: () => void;
  otpBusy: 'confirm' | 'resend' | null;
  otpError: ApiError | null;
}

function toneFor(status: ProviderResultStatus): string {
  if (status === ProviderResultStatus.APPROVED || status === ProviderResultStatus.CHOSEN) {
    return 'ok';
  }
  // Kod kutilayotgan holat ham «jarayonda»: hali rad etilgani yo'q.
  if (isPendingStatus(status)) return 'wait';
  return 'fail';
}

export function ResultCard({
  result,
  onChoose,
  choosing,
  decided,
  onConfirmOtp,
  onResendOtp,
  otpBusy,
  otpError,
}: ResultCardProps) {
  const { t, lang } = useI18n();
  const tone = toneFor(result.status);
  const chosen = result.status === ProviderResultStatus.CHOSEN;

  return (
    <article className={`result result--${tone} ${chosen ? 'result--chosen' : ''}`}>
      <header className="result__head">
        <h3 className="result__name">
          {PROVIDER_LABELS[result.provider]}
          {/* Namuna rejimi: bu limit haqiqiy emas */}
          {result.mode === 'mock' && (
            <span className="badge badge--warn result__mock">{t('result.mock')}</span>
          )}
        </h3>
        <span className={`result__status result__status--${tone}`}>
          {t(`status.${result.status}`)}
        </span>
      </header>

      {result.maxAmount !== null && (
        <div className="result__amount">
          <span className="result__amount-label">{t('result.limit')}</span>
          <span className="result__amount-value">{formatSoum(result.maxAmount, { lang })}</span>
        </div>
      )}

      {result.rejectReason && (
        <p className="result__reason">
          {/* Kalit bo'lsa — joriy tilda tarjima qilamiz: til almashtirilsa
              matn ham o'zgaradi, serverga qayta murojaat qilinmaydi. */}
          {result.rejectReasonKey
            ? translate(result.rejectReasonKey, lang)
            : result.rejectReason}
        </p>
      )}

      {result.otp && (
        <ResultOtp
          otp={result.otp}
          onConfirm={onConfirmOtp}
          onResend={onResendOtp}
          busy={otpBusy}
          error={otpError}
        />
      )}

      {result.limits.length > 0 && (
        <table className="result__limits">
          <thead>
            <tr>
              <th>{t('result.duration')}</th>
              <th>{t('result.monthly')}</th>
              <th>{t('result.markup')}</th>
            </tr>
          </thead>
          <tbody>
            {result.limits.map((limit) => (
              <tr key={limit.conditionId}>
                <td>{t('result.months', { n: limit.duration })}</td>
                <td>{limit.monthlyPayment ? formatSoum(limit.monthlyPayment, { lang }) : '—'}</td>
                <td>{limit.markupPercent ? `${limit.markupPercent}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="result__foot">
        {result.durationMs !== null && (
          <span className="result__timing">
            {t('unit.seconds', { n: (result.durationMs / 1000).toFixed(1) })}
          </span>
        )}
        {isSelectable(result.status) && !decided && (
          <button className="btn btn--primary" type="button" onClick={onChoose} disabled={choosing}>
            {choosing ? t('result.choosing') : t('result.choose')}
          </button>
        )}
        {chosen && <span className="result__chosen-mark">{t('result.chosen')}</span>}
      </footer>
    </article>
  );
}
